import EditUserDetail from 'components/EditUserDetail/EditUserDetail';
import { isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { UseFormReturn, Controller, useWatch } from 'react-hook-form';
import { IChannelRecording, InheritedRecording } from 'components/ProgramSetupChannelSettings';
import { IProgram } from 'shared/interfaces/IProgram';
import { useProgramTypeOptions } from 'shared/hooks/useProgramTypeOptions';
import { SEGMENT_TYPES } from 'shared/constants/SEGMENT_TYPES';
import { IProgramSetup, IProgramSetupForm, ProgramSetupUser } from 'shared/interfaces/IProgramSetup';
import { Select } from 'shared/theme/elements';
import { AnchorMultiSelect } from './components/AnchorMultiSelect';
import {
  Card,
  CardBody,
  CardHead,
  CardIc,
  CardSub,
  CardTitle,
  CardTitles,
  Fld,
  Grid3,
  IdxPill,
} from './ProgramSetup.styled';
import {
  ProgramSetupPageSearchLoading,
  ProgramSetupPageSearchOptions,
  ProgramSetupSearch,
} from './ProgramSetupContainer';

interface Props {
  form: UseFormReturn<IProgramSetupForm, object>;
  isLoading?: boolean;
  options: ProgramSetupPageSearchOptions;
  searchLoading: ProgramSetupPageSearchLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
  programSetup?: IProgramSetup;
  setPublisherId: React.Dispatch<React.SetStateAction<string>>;
  inheritedRecording?: IChannelRecording;
  // The selected program, which owns the Program Type.
  program?: IProgram;
}

export const ProgramSetupDetails = ({
  form,
  isLoading,
  options,
  searchLoading,
  onSearch,
  programSetup,
  setPublisherId,
  inheritedRecording,
  program,
}: Props) => {
  const {
    getValues,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = form;
  const stationObj = useWatch({ control, name: 'station_obj' });
  const programType = useWatch({ control, name: 'program_type' });
  const programId = useWatch({ control, name: 'station_program_obj' });
  const programTypes = useProgramTypeOptions({ value: program?.program_type, label: program?.program_type_label });
  const percentage =
    programTypes.getPercentage(programType) ??
    (programType && programType === program?.program_type ? program?.program_type_percentage : null);
  const stationName = options.radioStation?.find((option) => option.value === stationObj)?.label as string | undefined;
  const [programLabel, setProgramLabel] = useState('');

  const isUpdate = useMemo(() => !!programSetup, [programSetup]);

  const handleRadioStationChange = (value: any) => {
    setPublisherId(value);
    setValue('station_obj', value);
  };

  const getProgramLabel = (labelValue: string) => {
    const label = options.radioStationProgram?.filter((item: any) => item.value === labelValue);
    return label;
  };

  const handleRadioProgramChange = (value: any) => {
    setValue('station_program_obj', value ? (value as string) : null);
    setValue('program_obj', value ? (value as string) : null);

    const newLabel = getProgramLabel(value);
    setProgramLabel(newLabel?.[0]?.label as string);
  };

  const handleCountryChange = (value: any) => {
    clearErrors('country');
    setValue('country', value as string);
  };

  const handleSegmentTypeChange = (value: any) => {
    clearErrors('segment_type');
    setValue('segment_type', value ? (value as string) : null);
  };

  const getUserName = (user?: ProgramSetupUser) => {
    return user ? `${user.first_name} ${user.last_name}` : '';
  };

  const getLabel = (array: any, key: any) => {
    const item = array.filter((entry: any) => entry.value === getValues(key));
    return item;
  };

  const stationDisabled = isLoading || isEmpty(getValues().station_obj) ? true : false;

  return (
    <Card id="secDetails">
      <CardHead>
        <CardIc>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6M8 13h8M8 17h5" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Program Details</CardTitle>
          <CardSub>Basic information about the program</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        {isUpdate && (
          <Grid3 style={{ marginBottom: 20 }}>
            <EditUserDetail
              label="Created by"
              date={new Date(programSetup?.date_created || '')}
              name={getUserName(programSetup?.created_by?.[0])}
            />
            {programSetup && programSetup?.updated_by !== null && (
              <EditUserDetail
                label="Modified by"
                date={new Date(programSetup?.date_updated || '')}
                name={getUserName(programSetup?.updated_by?.[0])}
              />
            )}
          </Grid3>
        )}

        <Grid3>
          <Fld>
            <Select
              label="Radio Station"
              placeHolder="Select Radio Station"
              size="middle"
              value={getValues('station_obj')}
              options={options.radioStation || []}
              onChange={handleRadioStationChange}
              errorMessage={errors.station_obj?.message}
              onSearch={(keyword) => onSearch('radioStation', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.radioStation}
              allowClear
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Select
              label="Radio Program"
              placeHolder="Select Radio Program"
              size="middle"
              value={programLabel || getLabel(options.radioStationProgram || [], 'station_program_obj')}
              options={options.radioStationProgram || []}
              onChange={handleRadioProgramChange}
              errorMessage={errors.station_program_obj?.message}
              disabled={stationDisabled}
              onSearch={(keyword) => onSearch('radioStationProgram', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              allowClear
              loading={searchLoading.radioStationProgram}
              $fullWidth
              isRequired={!isEmpty(getValues().station_obj)}
            />
          </Fld>
          <Fld>
            <Select
              label="Segment Type"
              placeHolder="Select Segment Type"
              size="middle"
              value={isEmpty(getValues().segment_type) ? undefined : getValues().segment_type}
              options={SEGMENT_TYPES}
              onChange={handleSegmentTypeChange}
              errorMessage={errors.segment_type?.message}
              disabled={isLoading || isEmpty(getValues().station_program_obj) ? true : false}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="program_type"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  label="Program Type"
                  placeHolder="Select Program Type"
                  size="middle"
                  value={value || undefined}
                  options={programTypes.options}
                  loading={programTypes.loading}
                  onChange={(selected) => {
                    clearErrors('program_type');
                    onChange(selected || null);
                  }}
                  onBlur={onBlur}
                  errorMessage={errors.program_type?.message}
                  disabled={stationDisabled || isEmpty(programId)}
                  isRequired
                  $fullWidth
                />
              )}
            />
            <IdxPill>
              <span className="k">PERCENTAGE</span>
              <span className="v ps-mono">
                {percentage === null || percentage === undefined ? '—' : `${percentage}%`}
              </span>
            </IdxPill>
          </Fld>
          <Fld>
            <Select
              label="Country"
              placeHolder="Select Country"
              size="middle"
              value={isEmpty(getValues().country) ? undefined : getValues().country}
              options={options.country || []}
              onChange={handleCountryChange}
              errorMessage={errors.country?.message}
              disabled={stationDisabled}
              onSearch={(keyword) => onSearch('country', keyword)}
              showSearch
              filterOption={false}
              autoClearSearchValue
              loading={searchLoading.country}
              isRequired
              $fullWidth
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.on_off"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  label="Status"
                  size="middle"
                  value={String(value ?? 1)}
                  items={[
                    { id: '1', value: 'Active' },
                    { id: '0', value: 'Inactive' },
                  ]}
                  onChange={(selected) => onChange(Number(selected))}
                  onBlur={onBlur}
                  disabled={stationDisabled}
                  isRequired
                  $fullWidth
                />
              )}
            />
          </Fld>
          <Fld>
            <Controller
              control={control}
              name="recording_title_setup.sagap_process"
              render={({ field: { value, onChange, onBlur } }) => (
                <Select
                  label="Is From Sagap Process"
                  size="middle"
                  value={String(!!value)}
                  items={[
                    { id: 'true', value: 'Yes' },
                    { id: 'false', value: 'No' },
                  ]}
                  onChange={(selected) => onChange(selected === 'true')}
                  onBlur={onBlur}
                  disabled={stationDisabled}
                  isRequired
                  $fullWidth
                />
              )}
            />
          </Fld>
        </Grid3>

        <InheritedRecording label="Station" channelName={stationName} recording={inheritedRecording} />
      </CardBody>
    </Card>
  );
};

interface AnchorsProps {
  form: UseFormReturn<IProgramSetupForm, object>;
  isLoading?: boolean;
  options: ProgramSetupPageSearchOptions;
  searchLoading: ProgramSetupPageSearchLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
}

export const ProgramSetupAnchors = ({ form, isLoading, options, searchLoading, onSearch }: AnchorsProps) => {
  const {
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = form;
  const stationDisabled = isLoading || isEmpty(getValues().station_obj) ? true : false;

  return (
    <Card id="secAnchors">
      <CardHead>
        <CardIc $tone="ppl">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </CardIc>
        <CardTitles>
          <CardTitle>Anchor / Reporter</CardTitle>
          <CardSub>Select one or more anchors or reporters</CardSub>
        </CardTitles>
      </CardHead>
      <CardBody>
        <AnchorMultiSelect
          value={getValues().anchors}
          options={options.authors}
          onChange={(anchors) => {
            setValue('anchors', anchors);
            clearErrors('anchors');
          }}
          onSearch={(keyword) => onSearch('authors', keyword)}
          loading={searchLoading.authors}
          disabled={stationDisabled}
        />
        {errors.anchors?.message && (
          <div style={{ marginTop: 7, fontSize: 11.5, color: 'var(--req)', fontWeight: 600 }}>
            {errors.anchors?.message as string}
          </div>
        )}
      </CardBody>
    </Card>
  );
};
